import React, { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import StaticPageComponent from '@fuww/library/src/StaticPage';
import PageCardContainer from '@fuww/library/src/PageCardContainer';
import AdaptiveCard from '@fuww/library/src/Cards/AdaptiveCard';
import breakpoints from '@fuww/library/src/Theme/breakpoints';
import Head from '../components/Head';
import messages from '../lib/messages.json';
import Layout from '../components/Layout';
import Hero from '../components/Hero';
import HomeLink from '../components/HomeLink';
import LinkAndAnchor from '../components/LinkAndAnchor';
import statisticPages from '../lib/statisticPagesConfig';
import { companyProfilesCardImageConfigurations }
  from '../lib/imageConfigurations';
import ImageWithUrls from '../components/ImageWithUrls';
import getProxiedImages from '../lib/getProxiedImages';
import redirectRouteToDomain from '../lib/redirectRouteToDomain';

interface CardsDataProperties {
  slug?: string;
  title: string;
  imageUrls: string[];
  route: string;
}

const StatisticsPage = () => {
  const intl = useIntl();

  const breadcrumbs = [
    <HomeLink />,
    <LinkAndAnchor
      route="statistics"
      passHref
    >
      {intl.formatMessage(messages.statistics)}
    </LinkAndAnchor>,
  ];

  const originalCardsData: CardsDataProperties[] = Object.entries(
    statisticPages).map(([slug, data]) => ({
    route: 'statistic',
    slug,
    title: data.title,
    imageUrls: data.headerImageUrls.mobile,
  }));

  const cardsData = [...originalCardsData, {
    route: 'global-fashion-industry-statistics',
    title: 'Global Fashion Statistics',
    imageUrls: [
      'https://r.fashionunited.com/MTahNFyiGyfJsAru8_il_5LhLzr8SXyenUkPZDrAJIM/resize:fill:42:34:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS90dXJrZXktc3RhdGlzdGljcy1iYW5uZXItaW1hZ2UtbW9iaWxlLmpwZw',
      'https://r.fashionunited.com/JwUyJbgYyCozYmY0k2S411rOydx9lJfUotXrDb0Cs08/resize:fill:360:288:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS90dXJrZXktc3RhdGlzdGljcy1iYW5uZXItaW1hZ2UtbW9iaWxlLmpwZw',
      'https://r.fashionunited.com/3FlL4dJCRmC5vC94qbAKsvlQLsXr_RSGMBtsbprfBko/resize:fill:720:576:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS90dXJrZXktc3RhdGlzdGljcy1iYW5uZXItaW1hZ2UtbW9iaWxlLmpwZw',
    ],
  }];

  return (
    <>
      <Head
        title={intl.formatMessage(
          messages['statistics.title'],
        )}
        description={intl.formatMessage(
          messages['statistics.description'],
        )}
      />
      <StaticPageComponent
        breadcrumbs={breadcrumbs}
        content={(
          <PageCardContainer pageCards={cardsData.map(({
            route,
            slug,
            imageUrls: originalCardImageUrls,
            title,
          }) => {
            const { imageUrls: cardImageUrls, srcSet } = getProxiedImages(
              { imageConfigurations: companyProfilesCardImageConfigurations },
              originalCardImageUrls,
            );

            return (
              <LinkAndAnchor
                key={title}
                route={route}
                params={slug ? { slug } : {}}
                display="flex"
              >
                <AdaptiveCard
                  title={title}
                  textAlign="left"
                  image={cardImageUrls[0] && (
                    <ImageWithUrls
                      sizes={`
                        (min-width: 1440px) 318px,
                        (min-width: ${breakpoints.lg}px) calc(25vw - 42px),
                        (min-width: ${breakpoints.md}px) calc(50vw - 52px),
                        calc(100vw - 72px)
                      `}
                      srcSet={srcSet}
                      imageUrls={cardImageUrls}
                      alt={title}
                      disableRoundedCorners
                    />
                  )}
                  withPadding
                />
              </LinkAndAnchor>
            );
          })}
          />
        )}
        title={intl.formatMessage(
          messages['statistics.title'],
        )}
        subTitle={intl.formatMessage(
          messages['statistic.subTitle'],
        )}
      />
    </>
  );
};

StatisticsPage.getLayout = (page: ReactElement) => (
  <Layout header={<Hero />}>
    {page}
  </Layout>
);

StatisticsPage.getInitialProps = redirectRouteToDomain('statistics');

export default StatisticsPage;
